
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mockBackend } from '../services/mockBackend';
import { compressImage } from '../utils/imageCompressor';
import { Plus, Trash2, Tag, Layers, Settings as SettingsIcon, Edit2, X, Truck, Check, Users as UsersIcon, Palette, Database } from 'lucide-react';
import Users from './Users';

import { AlertModal } from './AlertModal';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'cadastros' | 'usuarios' | 'aparencia'>('cadastros');

  const [alertConfig, setAlertConfig] = useState<{isOpen: boolean, message: string, type: 'error'|'success'|'info'}>({isOpen: false, message: '', type: 'error'});
  const showAlert = (message: string, type: 'error'|'success'|'info' = 'error') => setAlertConfig({isOpen: true, message, type});

  const [categories, setCategories] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<string[]>([]);
  
  const [loading, setLoading] = useState(true);

  // States for Modal "Add New"
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<'category' | 'type' | 'vehicle' | null>(null);
  const [newItemValue, setNewItemValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // States for Inline Editing
  const [editingCategory, setEditingCategory] = useState<{original: string, temp: string} | null>(null);
  const [editingType, setEditingType] = useState<{original: string, temp: string} | null>(null);
  const [editingVehicle, setEditingVehicle] = useState<{original: string, temp: string} | null>(null);

  // States for Appearance
  const [headerText, setHeaderText] = useState('Gestão Fácil');
  const [headerSubtitle, setHeaderSubtitle] = useState('By Alonedev.com');

  // States for Company Settings
  const [companyName, setCompanyName] = useState('ENGMONT ENGENHARIA');
  const [companyLegalName, setCompanyLegalName] = useState('GEOVANI JOSE DE SOUZA EIRELI');
  const [companyCnpj, setCompanyCnpj] = useState('41.906.762/0001-39');
  const [companyAddress1, setCompanyAddress1] = useState('Rua Zulmira Borba, 361 - Nova Lima');
  const [companyAddress2, setCompanyAddress2] = useState('Campo Grande - MS | CEP 79017-101');
  const [companyEmail, setCompanyEmail] = useState('geovani@engmontengenharia.com.br');
  const [companyPhone, setCompanyPhone] = useState('+55(67) 99609-7857');
  const [reportLogoUrl, setReportLogoUrl] = useState('https://i.ibb.co/4n731KHh/LOGO-jpeg.jpg');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  useEffect(() => {
    loadData();
    // Load appearance settings if they exist (mocked for now)
    const savedHeader = localStorage.getItem('app_header_text');
    const savedSubtitle = localStorage.getItem('app_header_subtitle');
    if (savedHeader) setHeaderText(savedHeader);
    if (savedSubtitle) setHeaderSubtitle(savedSubtitle);

    // Load company settings
    const savedCompanyName = localStorage.getItem('company_name');
    const savedCompanyLegalName = localStorage.getItem('company_legal_name');
    const savedCompanyCnpj = localStorage.getItem('company_cnpj');
    const savedCompanyAddress1 = localStorage.getItem('company_address1');
    const savedCompanyAddress2 = localStorage.getItem('company_address2');
    const savedCompanyEmail = localStorage.getItem('company_email');
    const savedCompanyPhone = localStorage.getItem('company_phone');
    const savedReportLogoUrl = localStorage.getItem('report_logo_url');

    if (savedCompanyName) setCompanyName(savedCompanyName);
    if (savedCompanyLegalName) setCompanyLegalName(savedCompanyLegalName);
    if (savedCompanyCnpj) setCompanyCnpj(savedCompanyCnpj);
    if (savedCompanyAddress1) setCompanyAddress1(savedCompanyAddress1);
    if (savedCompanyAddress2) setCompanyAddress2(savedCompanyAddress2);
    if (savedCompanyEmail) setCompanyEmail(savedCompanyEmail);
    if (savedCompanyPhone) setCompanyPhone(savedCompanyPhone);
    if (savedReportLogoUrl) setReportLogoUrl(savedReportLogoUrl);
  }, []);

  const loadData = async () => {
    setLoading(true);
    const cats = await mockBackend.getCategories();
    const typs = await mockBackend.getTypes();
    const vecs = await mockBackend.getVehicles();
    setCategories(cats);
    setTypes(typs);
    setVehicles(vecs);
    setLoading(false);
  };

  // --- Modal Handlers ---
  const openAddModal = (section: 'category' | 'type' | 'vehicle') => {
      setActiveSection(section);
      setNewItemValue('');
      setIsModalOpen(true);
  };

  const handleSaveNewItem = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!newItemValue.trim() || !activeSection) return;
      
      setIsSaving(true);
      try {
          if (activeSection === 'category') await mockBackend.addCategory(user!, newItemValue);
          if (activeSection === 'type') await mockBackend.addType(user!, newItemValue);
          if (activeSection === 'vehicle') await mockBackend.addVehicle(user!, newItemValue);
          
          await loadData(); // Reload to reflect changes
          setIsModalOpen(false);
          setNewItemValue('');
      } catch (err: any) {
          showAlert("Erro ao adicionar: " + err.message);
      } finally {
          setIsSaving(false);
      }
  };

  // --- Update & Delete Handlers (Inline) ---

  // Categories
  const handleUpdateCategory = async () => {
    if (!editingCategory || !editingCategory.temp.trim()) return;
    try {
      await mockBackend.updateCategory(user!, editingCategory.original, editingCategory.temp);
      setEditingCategory(null);
      loadData();
    } catch (e: any) { showAlert(e.message); }
  };

  const handleDeleteCategory = async (cat: string) => {
    if (!confirm(`Excluir categoria "${cat}"?`)) return;
    try {
      await mockBackend.deleteCategory(user!, cat);
      loadData();
    } catch (e: any) { showAlert(e.message); }
  };

  // Types
  const handleUpdateType = async () => {
    if (!editingType || !editingType.temp.trim()) return;
    try {
      await mockBackend.updateType(user!, editingType.original, editingType.temp);
      setEditingType(null);
      loadData();
    } catch (e: any) { showAlert(e.message); }
  };

  const handleDeleteType = async (t: string) => {
    if (!confirm(`Excluir tipo "${t}"?`)) return;
    try {
      await mockBackend.deleteType(user!, t);
      loadData();
    } catch (e: any) { showAlert(e.message); }
  };

  // Vehicles
  const handleUpdateVehicle = async () => {
    if (!editingVehicle || !editingVehicle.temp.trim()) return;
    try {
      await mockBackend.updateVehicle(user!, editingVehicle.original, editingVehicle.temp);
      setEditingVehicle(null);
      loadData();
    } catch (e: any) { showAlert(e.message); }
  };

  const handleDeleteVehicle = async (v: string) => {
    if (!confirm(`Excluir veículo "${v}"?`)) return;
    try {
      await mockBackend.deleteVehicle(user!, v);
      loadData();
    } catch (e: any) { showAlert(e.message); }
  };

  const getSectionTitle = () => {
      switch(activeSection) {
          case 'category': return 'Nova Categoria';
          case 'type': return 'Novo Tipo de Lançamento';
          case 'vehicle': return 'Novo Veículo';
          default: return 'Novo Item';
      }
  };

  const handleSaveAppearance = () => {
    localStorage.setItem('app_header_text', headerText);
    localStorage.setItem('app_header_subtitle', headerSubtitle);
    
    localStorage.setItem('company_name', companyName);
    localStorage.setItem('company_legal_name', companyLegalName);
    localStorage.setItem('company_cnpj', companyCnpj);
    localStorage.setItem('company_address1', companyAddress1);
    localStorage.setItem('company_address2', companyAddress2);
    localStorage.setItem('company_email', companyEmail);
    localStorage.setItem('company_phone', companyPhone);
    localStorage.setItem('report_logo_url', reportLogoUrl);

    // Update document title and favicon immediately
    document.title = headerText;
    if (reportLogoUrl) {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = reportLogoUrl;
    }

    showAlert('Configurações salvas com sucesso!', 'success');
    window.location.reload();
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);
    try {
      const compressedBase64 = await compressImage(file);
      const uploadResult = await mockBackend.uploadImage(compressedBase64, file.name, file.type);
      setReportLogoUrl(uploadResult.url);
    } catch (error) {
      console.error('Upload error:', error);
      showAlert('Erro ao fazer upload da imagem.');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 font-sans">
      <AlertModal 
        isOpen={alertConfig.isOpen} 
        message={alertConfig.message} 
        type={alertConfig.type} 
        onClose={() => setAlertConfig(prev => ({...prev, isOpen: false}))} 
      />
      
      {/* Intro */}
      <div className="bg-white dark:bg-slate-900 p-5 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm flex items-start gap-4">
         <div className="bg-purple-100 p-3 rounded-full text-purple-600">
            <SettingsIcon size={24} />
         </div>
         <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">Configurações do Sistema</h2>
            <p className="text-slate-500 text-sm mt-1">
              Gerencie cadastros, usuários e a aparência do sistema.
            </p>
         </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        <button
          onClick={() => setActiveTab('cadastros')}
          className={`flex items-center gap-2 px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'cadastros'
              ? 'border-brand-600 text-brand-600 dark:text-brand-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          <Database size={18} /> Cadastros
        </button>
        {user?.role === 'admin' && (
          <button
            onClick={() => setActiveTab('usuarios')}
            className={`flex items-center gap-2 px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'usuarios'
                ? 'border-brand-600 text-brand-600 dark:text-brand-400'
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <UsersIcon size={18} /> Usuários
          </button>
        )}
        <button
          onClick={() => setActiveTab('aparencia')}
          className={`flex items-center gap-2 px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'aparencia'
              ? 'border-brand-600 text-brand-600 dark:text-brand-400'
              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
          }`}
        >
          <Palette size={18} /> Configurações Gerais
        </button>
      </div>

      {/* Tab Content: Cadastros */}
      {activeTab === 'cadastros' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-300">
           {/* Categories */}
           <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col h-[500px]">
              <div className="bg-slate-50 dark:bg-slate-950 p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                 <div className="flex items-center gap-2">
                     <Tag className="text-blue-600" size={18} />
                     <h3 className="font-bold text-base text-slate-700 dark:text-slate-300">Categorias</h3>
                 </div>
                 <button 
                   onClick={() => openAddModal('category')}
                   className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-sm transition-transform active:scale-95"
                   title="Adicionar Categoria"
                 >
                   <Plus size={16} />
                 </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2">
                 {categories.map(cat => (
                    <div key={cat} className="flex justify-between items-center p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors group">
                       {editingCategory?.original === cat ? (
                          <div className="flex flex-1 items-center gap-2 animate-in fade-in zoom-in duration-200">
                             <input 
                                type="text" 
                                value={editingCategory.temp}
                                onChange={e => setEditingCategory({...editingCategory, temp: e.target.value})}
                                className="flex-1 border border-blue-300 dark:border-blue-700 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 shadow-sm"
                                autoFocus
                             />
                             <button onClick={handleUpdateCategory} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 p-1.5 bg-green-50 dark:bg-green-900/30 rounded"><Check size={16}/></button>
                             <button onClick={() => setEditingCategory(null)} className="text-slate-400 hover:text-slate-600 dark:text-slate-400 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"><X size={16}/></button>
                          </div>
                       ) : (
                          <>
                             <span className="text-slate-700 dark:text-slate-300 text-sm font-medium pl-1">{cat}</span>
                             {user?.role === 'admin' && (
                                <div className="flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                                   <button onClick={() => setEditingCategory({original: cat, temp: cat})} className="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 p-1.5 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded">
                                      <Edit2 size={14} />
                                   </button>
                                   <button onClick={() => handleDeleteCategory(cat)} className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
                                      <Trash2 size={14} />
                                   </button>
                                </div>
                             )}
                          </>
                       )}
                    </div>
                 ))}
                 {categories.length === 0 && <div className="p-4 text-center text-slate-400 text-sm italic">Nenhuma categoria cadastrada.</div>}
              </div>
           </div>

           {/* Types */}
           <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col h-[500px]">
              <div className="bg-slate-50 dark:bg-slate-950 p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                 <div className="flex items-center gap-2">
                     <Layers className="text-purple-600 dark:text-purple-400" size={18} />
                     <h3 className="font-bold text-base text-slate-700 dark:text-slate-300">Tipos</h3>
                 </div>
                 <button 
                   onClick={() => openAddModal('type')}
                   className="p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 shadow-sm transition-transform active:scale-95"
                   title="Adicionar Tipo"
                 >
                   <Plus size={16} />
                 </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2">
                 {types.map(t => (
                    <div key={t} className="flex justify-between items-center p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors group">
                       {editingType?.original === t ? (
                          <div className="flex flex-1 items-center gap-2 animate-in fade-in zoom-in duration-200">
                             <input 
                                type="text" 
                                value={editingType.temp}
                                onChange={e => setEditingType({...editingType, temp: e.target.value})}
                                className="flex-1 border border-purple-300 dark:border-purple-700 rounded px-2 py-1 text-sm focus:outline-none focus:border-purple-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 shadow-sm"
                                autoFocus
                             />
                             <button onClick={handleUpdateType} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 p-1.5 bg-green-50 dark:bg-green-900/30 rounded"><Check size={16}/></button>
                             <button onClick={() => setEditingType(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"><X size={16}/></button>
                          </div>
                       ) : (
                          <>
                             <span className="text-slate-700 dark:text-slate-300 text-sm font-medium pl-1">{t}</span>
                             {user?.role === 'admin' && (
                                <div className="flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                                   <button onClick={() => setEditingType({original: t, temp: t})} className="text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 p-1.5 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded">
                                      <Edit2 size={14} />
                                   </button>
                                   <button onClick={() => handleDeleteType(t)} className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
                                      <Trash2 size={14} />
                                   </button>
                                </div>
                             )}
                          </>
                       )}
                    </div>
                 ))}
              </div>
           </div>

           {/* Vehicles */}
           <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col h-[500px]">
              <div className="bg-slate-50 dark:bg-slate-950 p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                 <div className="flex items-center gap-2">
                     <Truck className="text-orange-600 dark:text-orange-400" size={18} />
                     <h3 className="font-bold text-base text-slate-700 dark:text-slate-300">Veículos</h3>
                 </div>
                 <button 
                   onClick={() => openAddModal('vehicle')}
                   className="p-2 bg-orange-600 text-white rounded-full hover:bg-orange-700 shadow-sm transition-transform active:scale-95"
                   title="Adicionar Veículo"
                 >
                   <Plus size={16} />
                 </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2">
                 {vehicles.map(v => (
                    <div key={v} className="flex justify-between items-center p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors group">
                       {editingVehicle?.original === v ? (
                          <div className="flex flex-1 items-center gap-2 animate-in fade-in zoom-in duration-200">
                             <input 
                                type="text" 
                                value={editingVehicle.temp}
                                onChange={e => setEditingVehicle({...editingVehicle, temp: e.target.value})}
                                className="flex-1 border border-orange-300 dark:border-orange-700 rounded px-2 py-1 text-sm focus:outline-none focus:border-orange-500 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 shadow-sm"
                                autoFocus
                             />
                             <button onClick={handleUpdateVehicle} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 p-1.5 bg-green-50 dark:bg-green-900/30 rounded"><Check size={16}/></button>
                             <button onClick={() => setEditingVehicle(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"><X size={16}/></button>
                          </div>
                       ) : (
                          <>
                             <span className="text-slate-700 dark:text-slate-300 text-sm font-medium pl-1">{v}</span>
                             {user?.role === 'admin' && (
                                <div className="flex gap-1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                                   <button onClick={() => setEditingVehicle({original: v, temp: v})} className="text-slate-400 hover:text-orange-600 dark:hover:text-orange-400 p-1.5 hover:bg-orange-50 dark:hover:bg-orange-900/30 rounded">
                                      <Edit2 size={14} />
                                   </button>
                                   <button onClick={() => handleDeleteVehicle(v)} className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded">
                                      <Trash2 size={14} />
                                   </button>
                                </div>
                             )}
                          </>
                       )}
                    </div>
                 ))}
              </div>
           </div>
        </div>
      )}

      {/* Tab Content: Usuários */}
      {activeTab === 'usuarios' && user?.role === 'admin' && (
        <div className="animate-in fade-in duration-300">
          <Users />
        </div>
      )}

      {/* Tab Content: Aparência */}
      {activeTab === 'aparencia' && (
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm p-6 max-w-4xl animate-in fade-in duration-300">
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Coluna 1: Sistema */}
            <div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-6 border-b border-slate-100 dark:border-slate-800 pb-2">Personalização do Sistema</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Nome do Sistema (Cabeçalho)</label>
                  <input 
                    type="text" 
                    value={headerText}
                    onChange={(e) => setHeaderText(e.target.value)}
                    className="w-full border border-slate-300 dark:border-slate-700 p-3 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Ex: Gestão Fácil"
                  />
                  <p className="text-xs text-slate-500 mt-2">Este nome aparecerá no topo do menu de navegação.</p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Subtítulo do Sistema (Abaixo do Nome)</label>
                  <input 
                    type="text" 
                    value={headerSubtitle}
                    onChange={(e) => setHeaderSubtitle(e.target.value)}
                    className="w-full border border-slate-300 dark:border-slate-700 p-3 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    placeholder="Ex: Painel Web"
                  />
                  <p className="text-xs text-slate-500 mt-2">Este subtítulo aparecerá abaixo do nome do sistema.</p>
                </div>
              </div>
            </div>

            {/* Coluna 2: Relatórios */}
            <div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-6 border-b border-slate-100 dark:border-slate-800 pb-2">Dados para Relatórios</h3>
              <div className="space-y-4">
                
                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Logo do Sistema e Relatórios</label>
                  <div className="flex items-center gap-4">
                    {reportLogoUrl && (
                      <div className="w-16 h-16 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900 flex items-center justify-center overflow-hidden shrink-0">
                        <img src={reportLogoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={isUploadingLogo}
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 dark:file:bg-brand-900 dark:file:text-brand-300"
                      />
                      {isUploadingLogo && <p className="text-xs text-brand-600 mt-1">Fazendo upload...</p>}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Nome Fantasia</label>
                  <input 
                    type="text" 
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Razão Social</label>
                  <input 
                    type="text" 
                    value={companyLegalName}
                    onChange={(e) => setCompanyLegalName(e.target.value)}
                    className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">CNPJ</label>
                  <input 
                    type="text" 
                    value={companyCnpj}
                    onChange={(e) => setCompanyCnpj(e.target.value)}
                    className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Endereço Linha 1</label>
                  <input 
                    type="text" 
                    value={companyAddress1}
                    onChange={(e) => setCompanyAddress1(e.target.value)}
                    className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Endereço Linha 2</label>
                  <input 
                    type="text" 
                    value={companyAddress2}
                    onChange={(e) => setCompanyAddress2(e.target.value)}
                    className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">E-mail</label>
                    <input 
                      type="email" 
                      value={companyEmail}
                      onChange={(e) => setCompanyEmail(e.target.value)}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">Telefone</label>
                    <input 
                      type="text" 
                      value={companyPhone}
                      onChange={(e) => setCompanyPhone(e.target.value)}
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded-lg text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 mt-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <button 
              onClick={handleSaveAppearance}
              className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-8 rounded-lg shadow-sm transition-colors"
            >
              Salvar Configurações
            </button>
          </div>
        </div>
      )}

      {/* Modal for Add New */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
           <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 shadow-xl w-full max-w-md rounded-lg">
              <div className="bg-slate-800 dark:bg-slate-950 text-white px-6 py-3 flex justify-between items-center rounded-t-lg">
                 <h3 className="font-bold text-sm uppercase">{getSectionTitle()}</h3>
                 <button onClick={() => setIsModalOpen(false)}><X size={20}/></button>
              </div>
              <form onSubmit={handleSaveNewItem} className="p-6 space-y-4">
                 <div>
                    <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">Nome</label>
                    <input 
                      type="text" 
                      required 
                      autoFocus
                      value={newItemValue} 
                      onChange={e => setNewItemValue(e.target.value)} 
                      className="w-full border border-slate-300 dark:border-slate-700 p-2 rounded text-sm bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-brand-500" 
                      placeholder="Digite o nome..."
                    />
                 </div>
                 <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800 mt-4">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded transition-colors">Cancelar</button>
                    <button type="submit" disabled={isSaving} className="px-6 py-2 text-sm text-white bg-green-600 hover:bg-green-700 font-bold uppercase rounded shadow-sm disabled:opacity-50">
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </button>
                 </div>
              </form>
           </div>
        </div>
      )}

    </div>
  );
};

export default Settings;
